import React, { useState, useMemo, useEffect } from "react";
import { sileo } from "sileo";
import ModalPortal from "./ModalPortal";
import planAnualService from "@shared/api/planAnualService";
import AppSelect from "@/components/shared/AppSelect";
import "../../styles/modals/ModalBase.css";
import "../../styles/ModalActividadDetalle.css";

/**
 * Modal para mostrar detalles de una actividad
 * Soporta modo edición para roles admin (1) e inspector (2)
 * Renderizado usando Portal para evitar problemas de z-index
 */
const ActividadDetalleModal = ({
  actividad,
  isOpen,
  onClose,
  onVerReporte,
  canEdit = false,
  onActividadActualizada,
}) => {
  const [uiState, setUiState] = useState({ editing: false, loading: false });
  const [formData, setFormData] = useState(() => ({
    titulo: actividad?.titulo || "",
    descripcion: actividad?.descripcion || "",
    estado: actividad?.estado || "PENDIENTE",
    fechainicio: actividad?.fechainicio || "",
    fechafin: actividad?.fechafin || "",
    observaciones: actividad?.observaciones || "",
  }));
  const [reportesLocales, setReportesLocales] = useState(
    actividad?.reportes || [],
  );
  const [desvinculando, setDesvinculando] = useState(null);

  const estadoOptions = useMemo(
    () => [
      { value: "PENDIENTE", label: "Pendiente" },
      { value: "EN_PROGRESO", label: "En Progreso" },
      { value: "COMPLETADA", label: "Completada" },
      { value: "CANCELADA", label: "Cancelada" },
      { value: "REPROGRAMADA", label: "Reprogramada" },
    ],
    [],
  );

  useEffect(() => {
    const hasChanges =
      formData.titulo !== actividad.titulo ||
      formData.descripcion !== (actividad.descripcion || "") ||
      formData.estado !== actividad.estado ||
      formData.fechainicio !== actividad.fechainicio ||
      formData.fechafin !== actividad.fechafin ||
      formData.observaciones !== (actividad.observaciones || "");

    setUiState((prev) => ({ ...prev, hasChanges }));
  }, [formData, actividad]);

  const handleDesvincular = async (idReporte) => {
    if (desvinculando) return;

    const confirmar = window.confirm(
      `¿Estás seguro de desvincular el Reporte #${idReporte} de esta actividad?`,
    );
    if (!confirmar) return;

    setDesvinculando(idReporte);
    try {
      await planAnualService.desvincularReporte(
        actividad.idactividadprogramada,
        idReporte,
      );
      setReportesLocales((prev) =>
        prev.filter((r) => r.idreporte !== idReporte),
      );
      sileo.success({
        title: `Reporte #${idReporte} desvinculado correctamente`,
      });
      if (onActividadActualizada) onActividadActualizada();
    } catch (error) {
      console.error("Error al desvincular:", error);
      sileo.error({
        title: error.response?.data?.error || "Error al desvincular el reporte",
      });
    } finally {
      setDesvinculando(null);
    }
  };

  if (!actividad) return null;

  /**
   * Colores por estado
   */
  const getEstadoColor = (estado) => {
    const colores = {
      PENDIENTE: "#FFA500",
      EN_PROGRESO: "#4169E1",
      COMPLETADA: "#32CD32",
      CANCELADA: "#DC143C",
      VENCIDA: "#8B0000",
      REPROGRAMADA: "#9333ea",
    };
    return colores[estado] || "#808080";
  };

  /**
   * Manejar cambio en campos del formulario
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Guardar cambios
   */
  const handleGuardar = async () => {
    try {
      setUiState((p) => ({ ...p, loading: true }));

      // Construir objeto con solo los campos que cambiaron
      const cambios = {};

      if (actividad.esmanual && formData.titulo !== actividad.titulo) {
        cambios.titulo = formData.titulo;
      }
      if (formData.descripcion !== (actividad.descripcion || "")) {
        cambios.descripcion = formData.descripcion;
      }
      if (formData.estado !== actividad.estado) {
        cambios.estado = formData.estado;
      }
      if (formData.fechainicio !== actividad.fechainicio) {
        cambios.fechainicio = formData.fechainicio;
      }
      if (formData.fechafin !== actividad.fechafin) {
        cambios.fechafin = formData.fechafin;
      }
      if (formData.observaciones !== (actividad.observaciones || "")) {
        cambios.observaciones = formData.observaciones;
      }

      if (Object.keys(cambios).length === 0) {
        sileo.info({ title: "No hay cambios para guardar" });
        setUiState((p) => ({ ...p, editing: false }));
        return;
      }

      // Validaciones básicas
      if (cambios.fechainicio && cambios.fechafin) {
        if (new Date(cambios.fechafin) < new Date(cambios.fechainicio)) {
          sileo.error({
            title: "La fecha fin no puede ser anterior a la fecha inicio",
          });
          return;
        }
      } else if (cambios.fechafin && !cambios.fechainicio) {
        if (new Date(cambios.fechafin) < new Date(actividad.fechainicio)) {
          sileo.error({
            title: "La fecha fin no puede ser anterior a la fecha inicio",
          });
          return;
        }
      } else if (cambios.fechainicio && !cambios.fechafin) {
        if (new Date(actividad.fechafin) < new Date(cambios.fechainicio)) {
          sileo.error({
            title: "La fecha fin no puede ser anterior a la fecha inicio",
          });
          return;
        }
      }

      await planAnualService.actualizarActividad(
        actividad.idactividadprogramada,
        cambios,
      );

      sileo.success({ title: "Actividad actualizada correctamente" });
      setUiState((p) => ({ ...p, editing: false }));

      if (onActividadActualizada) {
        onActividadActualizada();
      }

      onClose();
    } catch (error) {
      console.error("Error al actualizar actividad:", error);
      sileo.error({
        title:
          error.response?.data?.error || "Error al actualizar la actividad",
      });
    } finally {
      setUiState((p) => ({ ...p, loading: false }));
    }
  };

  /**
   * Cancelar edición y restaurar valores originales
   */
  const handleCancelarEdicion = () => {
    setFormData({
      titulo: actividad.titulo || "",
      descripcion: actividad.descripcion || "",
      estado: actividad.estado || "PENDIENTE",
      fechainicio: actividad.fechainicio || "",
      fechafin: actividad.fechafin || "",
      observaciones: actividad.observaciones || "",
    });
    setUiState((p) => ({ ...p, editing: false }));
  };

  // Agrupar subcomponentes por componente
  const agruparSubcomponentesPorComponente = () => {
    if (!actividad.subcomponentes || actividad.subcomponentes.length === 0) {
      return null;
    }

    const grupos = {};
    actividad.subcomponentes.forEach((sub) => {
      const componenteNombre = sub.componente?.descripcion || "Sin componente";
      if (!grupos[componenteNombre]) {
        grupos[componenteNombre] = [];
      }
      grupos[componenteNombre].push(sub.descripcion);
    });

    return grupos;
  };

  const gruposComponentes = agruparSubcomponentesPorComponente();
  const tieneMultiplesComponentes =
    gruposComponentes && Object.keys(gruposComponentes).length > 1;

  // Para compatibilidad con vista simple (un solo componente/subcomponente)
  const componente =
    actividad.componente ||
    (actividad.subcomponentes && actividad.subcomponentes.length > 0
      ? actividad.subcomponentes[0].componente?.descripcion
      : "Sin componente");
  const subcomponente =
    actividad.subcomponente ||
    (actividad.subcomponentes && actividad.subcomponentes.length > 0
      ? actividad.subcomponentes[0].descripcion
      : "Sin subcomponente");

  return (
    <ModalPortal isOpen={isOpen}>
      <div
        role="button"
        tabIndex={0}
        className="act-detail-modal__overlay"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClose(e);
          }
        }}
      >
        <div
          role="button"
          tabIndex={0}
          className="act-detail-modal__content"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {/* Header */}
          <div className="act-detail-modal__header">
            <h3 className="act-detail-modal__header-title">
              <i
                className={`fas ${uiState.editing ? "fa-edit" : "fa-calendar-check"}`}
              ></i>
              {uiState.editing
                ? "Editar Actividad"
                : "Detalles de la Actividad"}
            </h3>
            <button className="act-detail-modal__close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Body */}
          <div className="act-detail-modal__body">
            {/* Título */}
            {uiState.editing && actividad.esmanual ? (
              <div className="act-detail-modal__group">
                <span className="act-detail-modal__label">
                  <i className="fas fa-heading"></i> Título
                </span>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  className="act-detail-modal__input"
                  disabled={uiState.loading}
                />
              </div>
            ) : (
              <h2 className="act-detail-modal__title">{actividad.titulo}</h2>
            )}

            {/* Badges: Estado y Tipo */}
            <div className="act-detail-modal__meta">
              {uiState.editing ? (
                <AppSelect
                  options={estadoOptions}
                  value={formData.estado}
                  onChange={(val) =>
                    handleChange({ target: { name: "estado", value: val } })
                  }
                  placeholder="Seleccionar estado..."
                  variant="modal"
                  menuPortalTarget={document.body}
                  isDisabled={uiState.loading}
                />
              ) : (
                <span
                  className="act-detail-modal__badge-estado"
                  style={{
                    backgroundColor:
                      getEstadoColor(actividad.estado) ||
                      actividad.color ||
                      "#808080",
                  }}
                >
                  {actividad.estado}
                </span>
              )}
              <span className="act-detail-modal__badge-tipo">
                {actividad.tipoMantenimiento?.tipomantenimiento ||
                  actividad.tipoMantenimiento ||
                  "N/A"}
              </span>
            </div>

            {/* Descripción */}
            <div className="act-detail-modal__group">
              <span className="act-detail-modal__label">
                <i className="fas fa-align-left"></i> Descripción
              </span>
              {uiState.editing ? (
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  className="act-detail-modal__input act-detail-modal__textarea"
                  rows="3"
                  placeholder="Descripción de la actividad..."
                  disabled={uiState.loading}
                />
              ) : actividad.descripcion ? (
                <p className="act-detail-modal__description">
                  {actividad.descripcion}
                </p>
              ) : (
                <p className="act-detail-modal__empty-text">Sin descripción</p>
              )}
            </div>

            {/* Fechas */}
            <div className="act-detail-modal__group">
              <span className="act-detail-modal__label">
                <i className="fas fa-calendar-alt"></i> Fecha Inicio
              </span>
              {uiState.editing ? (
                <input
                  type="date"
                  name="fechainicio"
                  value={formData.fechainicio}
                  onChange={handleChange}
                  className="act-detail-modal__input"
                  disabled={uiState.loading}
                />
              ) : (
                <p>
                  {new Date(
                    actividad.fechainicio + "T00:00:00",
                  ).toLocaleDateString("es-MX", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}
            </div>

            <div className="act-detail-modal__group">
              <span className="act-detail-modal__label">
                <i className="fas fa-calendar-check"></i> Fecha Fin
              </span>
              {uiState.editing ? (
                <input
                  type="date"
                  name="fechafin"
                  value={formData.fechafin}
                  onChange={handleChange}
                  className="act-detail-modal__input"
                  disabled={uiState.loading}
                />
              ) : (
                <p>
                  {new Date(
                    actividad.fechafin + "T00:00:00",
                  ).toLocaleDateString("es-MX", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}
            </div>

            {/* Componentes y Subcomponentes */}
            {tieneMultiplesComponentes ? (
              <div className="act-detail-modal__group">
                <span className="act-detail-modal__label">
                  <i className="fas fa-layer-group"></i> Componentes y
                  Subcomponentes
                </span>
                <div className="componentes-agrupados">
                  {Object.entries(gruposComponentes).map(
                    ([componente, subcomponentes]) => (
                      <div key={componente} className="grupo-componente">
                        <div className="componente-titulo">
                          <i className="fas fa-layer-group"></i>
                          <strong>{componente}</strong>
                        </div>
                        <div className="subcomponentes-list">
                          {subcomponentes.map((sub) => (
                            <span key={sub} className="subcomponente-chip">
                              {sub}
                            </span>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="act-detail-modal__group">
                  <span className="act-detail-modal__label">
                    <i className="fas fa-cogs"></i> Componente
                  </span>
                  <p className="act-detail-modal__card-value">{componente}</p>
                </div>

                {actividad.esMultiSubcomponente &&
                actividad.subcomponentesIncluidos?.length > 0 ? (
                  <div className="act-detail-modal__group">
                    <span className="act-detail-modal__label">
                      <i className="fas fa-puzzle-piece"></i> Subcomponentes
                    </span>
                    <div className="subcomponentes-list">
                      {actividad.subcomponentesIncluidos.map((sub) => (
                        <span key={sub.id} className="subcomponente-chip">
                          {sub.nombre}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="act-detail-modal__group">
                    <span className="act-detail-modal__label">
                      <i className="fas fa-puzzle-piece"></i> Subcomponente
                    </span>
                    <p className="act-detail-modal__card-value">
                      {subcomponente}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Observaciones */}
            <div className="act-detail-modal__group">
              <span className="act-detail-modal__label">
                <i className="fas fa-comment-alt"></i> Observaciones
              </span>
              {uiState.editing ? (
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange}
                  className="act-detail-modal__input act-detail-modal__textarea"
                  rows="3"
                  placeholder="Observaciones adicionales..."
                  disabled={uiState.loading}
                />
              ) : actividad.observaciones ? (
                <p>{actividad.observaciones}</p>
              ) : (
                <p className="act-detail-modal__empty-text">
                  Sin observaciones
                </p>
              )}
            </div>

            {/* Reportes asociados */}
            {(reportesLocales.length > 0 || actividad.idreporte) &&
              onVerReporte && (
                <div className="act-detail-modal__group">
                  <span className="act-detail-modal__label">
                    <i className="fas fa-file-alt"></i> Reportes Asociados
                    {reportesLocales.length > 0 && (
                      <span className="act-detail-modal__badge-count">
                        {reportesLocales.length}
                      </span>
                    )}
                  </span>
                  <div className="act-detail-modal__reportes-list">
                    {reportesLocales.length > 0 ? (
                      reportesLocales.map((reporte) => (
                        <div
                          key={reporte.idreporte}
                          className="act-detail-modal__reporte-item"
                        >
                          <button
                            className="act-detail-modal__reporte-btn"
                            onClick={() => onVerReporte(reporte.idreporte)}
                          >
                            <i className="fas fa-file-alt"></i>
                            Reporte #{reporte.idreporte}
                            {reporte.fechacreacion && (
                              <span className="act-detail-modal__reporte-date">
                                {new Date(
                                  reporte.fechacreacion,
                                ).toLocaleDateString("es-MX")}
                              </span>
                            )}
                          </button>
                          {canEdit && (
                            <button
                              className="act-detail-modal__reporte-unlink"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDesvincular(reporte.idreporte);
                              }}
                              disabled={desvinculando === reporte.idreporte}
                              title="Desvincular reporte"
                            >
                              <i
                                className={
                                  desvinculando === reporte.idreporte
                                    ? "fas fa-spinner fa-spin"
                                    : "fas fa-unlink"
                                }
                              ></i>
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <button
                        className="act-detail-modal__reporte-btn"
                        onClick={() => onVerReporte(actividad.idreporte)}
                      >
                        <i className="fas fa-file-alt"></i>
                        Ver Reporte de Mantenimiento
                      </button>
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Footer */}
          <div className="act-detail-modal__footer">
            {uiState.editing ? (
              <>
                <button
                  className="act-detail-modal__btn act-detail-modal__btn--cancel"
                  onClick={handleCancelarEdicion}
                  disabled={uiState.loading}
                >
                  <i className="fas fa-times"></i> Cancelar
                </button>
                <button
                  className="act-detail-modal__btn act-detail-modal__btn--primary"
                  onClick={handleGuardar}
                  disabled={uiState.loading || !uiState.hasChanges}
                >
                  {uiState.loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Guardando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Guardar Cambios
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  className="act-detail-modal__btn act-detail-modal__btn--close"
                  onClick={onClose}
                >
                  Cerrar
                </button>
                {canEdit && (
                  <button
                    className="act-detail-modal__btn act-detail-modal__btn--primary"
                    onClick={() => setUiState((p) => ({ ...p, editing: true }))}
                  >
                    <i className="fas fa-edit"></i> Editar
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ActividadDetalleModal;
